import { computedFn } from 'mobx-utils/lib/computedFn';
import { isFeatureEnabled } from 'owa-feature-flags';
import type { RibbonTabId, RibbonGroupId } from 'owa-ribbon-ids';
import { MailRibbonTabId, MailRibbonGroupId } from 'owa-ribbon-ids';
import type { MutableControlProps } from './MutableControlProps';
import type { RibbonControlDefinitionProps } from './RibbonControlDefinitionProps';

/**
 * This function decides whether a group should show or not.
 * For any logic on show/hiding groups, please put them here.
 *
 * @tabId the current Tab that is calling this function
 * @groupId the current group being tested to see whether it should show
 * @props Optional parameter, used for certain buttons to test whether they should show.
 */
export const shouldShowRibbonGroup = computedFn(
    (
        tab: RibbonTabId,
        groupId: RibbonGroupId,
        mutableProps: MutableControlProps,
        props: RibbonControlDefinitionProps | undefined
    ): boolean => {
        // Customization flight check
        if (groupId === MailRibbonGroupId.Group_RibbonCustomizer) {
            return isFeatureEnabled('mon-ribbon-customization') && !props?.popoutComposeViewState;
        }

        if (tab == MailRibbonTabId.Tab_Home) {
            switch (groupId) {
                case MailRibbonGroupId.Group_Insights:
                case MailRibbonGroupId.Group_Protection:
                case MailRibbonGroupId.Group_QuickSteps:
                case MailRibbonGroupId.Group_Speech:
                    return isFeatureEnabled('mon-ribbon-postSdf');

                case MailRibbonGroupId.Group_ToggleLeftPane:
                    return !isFeatureEnabled('mon-tri-collapsibleFolderPane');

                default:
                    return true;
            }
        } else if (tab == MailRibbonTabId.Tab_Message) {
            switch (groupId) {
                case MailRibbonGroupId.Group_Clipboard_Message:
                case MailRibbonGroupId.Group_ImmersiveReader:
                case MailRibbonGroupId.Group_Voice:
                case MailRibbonGroupId.Group_Speech:
                    return isFeatureEnabled('mon-ribbon-postSdf');

                case MailRibbonGroupId.Group_Sensitivity:
                    return mutableProps.isClpEnabled;

                case MailRibbonGroupId.Group_Encrypt:
                    return mutableProps.isEncryptionAvailable;

                default:
                    return true;
            }
        } else if (tab == MailRibbonTabId.Tab_View) {
            switch (groupId) {
                case MailRibbonGroupId.Group_View_Settings:
                case MailRibbonGroupId.Group_View_Messages:
                case MailRibbonGroupId.Group_View_Layout:
                    return isFeatureEnabled('mon-ribbon-postSdf');

                default:
                    return true;
            }
        }

        return true;
    }
);
